import type React from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Send, X, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/lib/hooks/use-toast'
import { useCreateEditAgreement } from '../services/useCreateEditAgreement'

interface RequestEditModalProps {
  idAgreement?: string
  onSubmit?: (text: string) => void
  onCancel?: () => void
  buttonLabel?: string
}

export default function RequestEditModal({
  idAgreement,
  onSubmit,
  onCancel,
  buttonLabel = 'Solicitar Alteração'
}: RequestEditModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const { toast } = useToast()

  const { mutateAsync, isPending } = useCreateEditAgreement(idAgreement)

  const handleSubmit = async () => {
    const text = inputValue.trim()
    if (!text) return
    try {
      await mutateAsync({ requestChange: text })
      onSubmit?.(text)
      toast({
        variant: 'success',
        title: 'Solicitação enviada',
        description: 'Sua solicitação foi registrada com sucesso.'
      })
      setInputValue('')
      setIsOpen(false)
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Não foi possível enviar',
        description: err?.message ?? 'Tente novamente em instantes.'
      })
    }
  }

  const handleCancel = () => {
    setInputValue('')
    setIsOpen(false)
    onCancel?.()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-foreground-secondary shadow-sm"
      >
        {buttonLabel}
      </Button>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={handleCancel} aria-hidden="true" />

      <Card className="relative w-full max-w-2xl shadow-2xl border-0 bg-white">
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-center justify-between">
            <CardTitle className="ml-3 text-xl flex items-center gap-2">Solicitar Alteração</CardTitle>
            <Button variant="ghost" size="icon" onClick={handleCancel} className="hover:bg-slate-100">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <p className="text-sm text-slate-600 mt-2">Descreva as alterações que você gostaria de ver no contrato</p>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-primary" />
              Descrição da Alteração
            </label>

            <Textarea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ex: Alterar a cláusula 2.1 para incluir/retirar..."
              className="w-full h-32 resize-none border-slate-200"
              autoFocus
              disabled={isPending}
            />

            <p className="text-xs text-slate-500">
              Pressione <kbd>Enter</kbd> para enviar ou <kbd>Shift</kbd>+<kbd>Enter</kbd> para quebrar linha
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isPending}
              className="hover:bg-slate-50 bg-transparent"
            >
              Cancelar
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isPending}
              className={cn(
                'flex items-center gap-2 bg-primary hover:bg-primary-hover text-foreground-secondary',
                isPending && 'opacity-75'
              )}
            >
              {isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar Solicitação
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
